import "fabric";
import {certificateFieldsCreate, certificateFieldsDelete, certificateFieldsUpdate, clientCertificateGet} from "api";

export default class CertificateEditor {
    constructor(canvasEl, {width, height}) {
        fabric.Group.prototype.hasControls = false;
        fabric.Object.prototype.cornerSize = 8;
        fabric.Object.prototype.transparentCorners = false;

        delete fabric.Textbox.prototype.controls.mtr;
        delete fabric.Textbox.prototype.controls.mt;
        delete fabric.Textbox.prototype.controls.mb;
        delete fabric.Textbox.prototype.controls.bl;
        delete fabric.Textbox.prototype.controls.br;
        delete fabric.Textbox.prototype.controls.tl;
        delete fabric.Textbox.prototype.controls.tr;

        this.canvas = new fabric.Canvas(canvasEl, {
            width,
            height,
            containerClass: 'certificate-canvas-container',
        });

        this.tool = null;
        this.fontSize = 12;
        this.fontFamily = 'Helvetica';
        this.textAlign = 'left';
        this.textColor = '#000000';
        this.isBold = false;
        this.isItalic = false;
        this.backgroundImage = null;
        this.event = null;
        this.canvasDimensions = {
            width,
            height,
        };
        this.onToolChange = null;
        this.onSelectionChange = null;
        this.onPropertyChange = null;

        this.canvas.on('object:modified', (options) => {
            let objects;
            if (options.target.hasOwnProperty('_objects')) {
                objects = options.target._objects;
            } else {
                objects = [options.target];
            }

            this.modifyObjects(objects);
        });

        // ограничение движения объектов рамками канваса
        this.canvas.on('object:moving', function (e) {
            var obj = e.target;

            // if object is too big ignore
            if(obj.getScaledHeight() > obj.canvas.height || obj.getScaledWidth() > obj.canvas.width){
                return;
            }
            obj.setCoords();
            // top-left  corner
            if(obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0){
                obj.top = Math.max(obj.top, obj.top-obj.getBoundingRect().top);
                obj.left = Math.max(obj.left, obj.left-obj.getBoundingRect().left);
            }
            // bot-right corner
            if(obj.getBoundingRect().top+obj.getBoundingRect().height  > obj.canvas.height || obj.getBoundingRect().left+obj.getBoundingRect().width  > obj.canvas.width){
                obj.top = Math.min(obj.top, obj.canvas.height-obj.getBoundingRect().height+obj.top-obj.getBoundingRect().top);
                obj.left = Math.min(obj.left, obj.canvas.width-obj.getBoundingRect().width+obj.left-obj.getBoundingRect().left);
            }
        });

        this.canvas.on('selection:created', (options) => {
            if (this.onSelectionChange) {
                this.onSelectionChange(this.canvas.getActiveObjects());
            }
        });
        this.canvas.on('selection:updated', (options) => {
            if (this.onSelectionChange) {
                this.onSelectionChange(this.canvas.getActiveObjects());
            }
        });
        this.canvas.on('selection:cleared', () => {
            if (this.onSelectionChange) {
                this.onSelectionChange([]);
            }
        });

        this.canvas.wrapperEl.addEventListener('click', this.handleClick.bind(this));

        document.onclick = () => {
            this.setTool(null);
        }

        document.addEventListener('keyup', (e) => {
            if ('Delete' === e.code) {
                this.deleteActiveObjects();
            }

            if (this.tool) {
                this.setTool(null);
            }
        });
    }

    setEvent(event) {
        this.event = event;

        clientCertificateGet(event.eventPassword)
            .then(data => {
                const {fields} = data;
                fields.forEach(field => {
                    field.textColor = '#' + field.textColor;
                    this.addText(field.text, field, field.id);
                });

                return this.setBackgroundImageFromUrl(data.url);
            })
        ;
    }

    handlePropertyChange(property, value) {
        if (this.onPropertyChange) {
            this.onPropertyChange(property, value);
        }

        let objectProperty = property;
        let objectValue = value;
        switch (property) {
            case 'fontSize':
                this.fontSize = value;
                break;
            case 'fontFamily':
                this.fontFamily = value;
                break;
            case 'textAlign':
                this.textAlign = value;
                break;
            case 'textColor':
                this.textColor = value;
                objectValue = value;
                objectProperty = 'fill';
                value = value.replace('#', '');
                break;
            case 'isBold':
                this.isBold = value;
                objectProperty = 'fontWeight';
                objectValue = value ? 'bold' : 'normal';
                break;
            case 'isItalic':
                this.isItalic = value;
                objectProperty = 'fontStyle';
                objectValue = value ? 'italic' : 'normal';
                break;
        }

        this.canvas.getActiveObjects().forEach(object => {
            object.set(objectProperty, objectValue);
        });

        const fieldsData = [];
        this.canvas.getActiveObjects().forEach(object => {
            const options = {id: object.id};
            options[property] = value;
            fieldsData.push(options);
        });

        certificateFieldsUpdate(this.event.eventPassword, {fields: fieldsData});
        this.canvas.renderAll();
    }

    modifyObjects(objects) {
        const data = [];

        objects.forEach(object => {
            data.push(this.transformObjectToData(object));
        });

        certificateFieldsUpdate(this.event.eventPassword, {fields: data});
    }

    setTool(tool) {
        this.tool = tool;
        if (this.onToolChange) {
            this.onToolChange(tool);
        }

        if (null === this.canvas) {
            return;
        }

        if (null === tool) {
            this.canvas.defaultCursor = 'default';
            this.canvas.skipTargetFind = false;
            this.canvas.setCursor(this.canvas.defaultCursor);

            return;
        }

        this.canvas.defaultCursor = 'crosshair';
        this.canvas.skipTargetFind = true;
    }

    deleteActiveObjects() {
        if (null === this.canvas) {
            return;
        }

        const ids = [];
        const scheduledRemove = [];
        this.canvas.getActiveObjects().forEach((object) => {
            if (object.isEditing) {
                return;
            }

            scheduledRemove.push(object);
            ids.push(object.id);
        });

        if (0 === scheduledRemove.length) {
            return;
        }

        this.canvas.remove(...scheduledRemove);
        this.canvas.discardActiveObject();
        this.canvas.renderAll();

        if (0 === ids.length) {
            return;
        }

        certificateFieldsDelete(this.event.eventPassword, ids);
    }

    calculateCanvasDimensions(imageWidth, imageHeight) {
        let maxCanvasWidth, maxCanvasHeight;
        if (imageWidth / imageHeight > 1.5) {
            maxCanvasWidth = window.screen.width - 100;
        } else if (imageWidth / imageHeight > 0.9) {
            maxCanvasWidth = window.screen.width - 200;
        } else {
            maxCanvasWidth = window.screen.width - 500;
        }
        maxCanvasHeight = imageHeight / imageWidth * maxCanvasWidth;

        if (imageWidth < maxCanvasWidth && imageHeight < maxCanvasHeight) {
            return {
                width: imageWidth,
                height: imageHeight
            };
        }

        return {
            width: maxCanvasWidth,
            height: maxCanvasHeight,
        };
    }

    setBackgroundImageFromUrl(url) {
        return new Promise((resolve, reject) => {
            fabric.Image.fromURL(url, image => {
                this.backgroundImage = image;
                this.canvas.setBackgroundImage(image);

                const {width, height} = this.calculateCanvasDimensions(image.width, image.height);
                this.canvasDimensions = {width, height};

                this.canvas.setDimensions({
                    width: image.width,
                    height: image.height,
                });
                this.canvas.upperCanvasEl.style.maxWidth = `${width}px`;
                this.canvas.upperCanvasEl.style.maxHeight = `${height}px`;

                this.canvas.lowerCanvasEl.style.maxWidth = `${width}px`;
                this.canvas.lowerCanvasEl.style.maxHeight = `${height}px`;

                this.canvas.wrapperEl.style.width = `${width}px`;
                this.canvas.wrapperEl.style.height = `${height}px`;

                this.canvas.renderAll();

                resolve(image);
            });
        });
    }

    handleClick(e) {
        if (null === this.tool) {
            return;
        }

        const x = e.offsetX / this.canvasDimensions.width * this.backgroundImage.width;
        const y = e.offsetY / this.canvasDimensions.height * this.backgroundImage.height;

        let [tool, ...args] = this.tool.split(':');

        switch (tool) {
            case 'text':
                this.addText(args[0] || '{placeholder}', {
                    x,
                    y,
                    fontSize: this.fontSize,
                    fontFamily: this.fontFamily,
                    textAlign: this.textAlign,
                    textColor: this.textColor,
                });

                break;
        }

        this.setTool(null);
        e.stopPropagation();
    }

    transformObjectToData(object) {
        let x, y;
        if (object.hasOwnProperty('group')) {
            let group = object.group;
            x = object.left + group.left + group.width / 2;
            y = object.top + group.top + group.height / 2;
        } else {
            x = object.left;
            y = object.top;
        }

        const data = {
            id: object.id,
            text: object.text,
            fontSize: object.fontSize,
            fontFamily: object.fontFamily,
            textColor: object.fill.replace('#', ''),
            isBold: object.fontWeight === 'bold',
            isItalic: object.fontStyle === 'italic',
            textAlign: object.textAlign,
            x,
            y,
            width: object.width,
        };

        if (object.id) {
            data.id = object.id;
        }

        return data;
    }

    addText(text, {x, y, fontSize, fontFamily, textAlign, textColor, width}, id) {
        const object = new fabric.Textbox(text, {
            left: x,
            top: y,
            fontSize,
            fontFamily,
            textAlign,
            fill: textColor,
        });

        if ('undefined' !== typeof width) {
            object.width = width;
        }

        if ('undefined' === typeof id) {
            certificateFieldsCreate(this.event.eventPassword, this.transformObjectToData(object))
                .then(data => {
                    object.id = data.id;
                    this.canvas.add(object);
                })
                .catch(() => {});
        } else {
            object.id = id;
            this.canvas.add(object);
        }
    }
}
